import { useState } from "react";
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, ExternalLink } from "lucide-react";
import { API } from "@/lib/api";
import axios from "axios";
import { usePageMeta } from "@/lib/usePageMeta";

const STATE_META = {
  broken: { label: "Broken", Icon: XCircle, cls: "text-red-600 bg-red-50 border-red-200" },
  blocked: { label: "Blocked / unverifiable", Icon: AlertTriangle, cls: "text-amber-600 bg-amber-50 border-amber-200" },
  ok: { label: "OK", Icon: CheckCircle2, cls: "text-green-600 bg-green-50 border-green-200" },
};

export default function AdminLinks() {
  usePageMeta("Link health — Admin | Fetchright", "Internal outbound link health checker.");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/link-check`, { timeout: 180000 });
      setData(res.data);
    } catch (e) {
      setError(e?.message || "Check failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <h1 className="font-display font-bold text-3xl md:text-4xl text-accent">Outbound link health</h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        Pings every external link we send visitors to — breeder registries, shelters, trainers, vets,
        insurance and starter-kit partners — and flags anything broken.
      </p>

      <button
        onClick={run}
        disabled={loading}
        data-testid="run-link-check"
        className="pill-btn mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-bold disabled:opacity-60"
      >
        <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        {loading ? "Checking all links…" : "Run link check"}
      </button>

      {error && (
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 text-red-700 p-4 text-sm" data-testid="link-check-error">
          {error}
        </div>
      )}

      {data && (
        <div className="mt-8" data-testid="link-check-results">
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="rounded-full bg-green-50 border border-green-200 text-green-700 px-4 py-1.5 text-sm font-semibold">
              {data.ok_count} OK
            </span>
            <span className="rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-4 py-1.5 text-sm font-semibold">
              {data.blocked_count} blocked
            </span>
            <span className="rounded-full bg-red-50 border border-red-200 text-red-700 px-4 py-1.5 text-sm font-semibold">
              {data.broken_count} broken
            </span>
          </div>

          <p className="text-xs text-muted-foreground mb-4">
            "Blocked" means the site responded but blocked our checker (bot protection / rate limit) — those links
            are almost always fine for real visitors. "Broken" (404 / dead domain) needs fixing. Note: some big sites
            may show as broken here due to network limits — always click through to confirm before changing.
          </p>

          <div className="space-y-2">
            {data.results.map((r) => {
              const meta = STATE_META[r.state] || STATE_META.ok;
              const Icon = meta.Icon;
              return (
                <div
                  key={r.url}
                  data-testid={`link-row-${r.state}`}
                  className="flex items-center gap-3 rounded-xl border border-border/60 bg-card p-3.5"
                >
                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold shrink-0 ${meta.cls}`}>
                    <Icon size={13} /> {r.status || r.error ? (r.status || "ERR") : meta.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-accent truncate">
                      {r.name} <span className="text-muted-foreground font-normal">· {r.category}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{r.url}</div>
                  </div>
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-primary hover:opacity-70"
                    title="Open link"
                  >
                    <ExternalLink size={16} />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
